import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { PeriodoDataCalendar } from '../components/PeriodoDataCalendar';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import {
  addEvent,
  getEvents,
  getEventById,
  saveEvent,
  removeEvent,
  getEventCategories,
  getEventTags,
  validateEvent,
  mergeEventDraftDefaults,
  TIMEZONES,
  type Event,
  type TicketType,
  type EventVisibility,
  type EventFormat,
  type EventPeriodoStatusTipo,
} from '../lib/eventsStorage';
import {
  hojeYmdLocal,
  MSG_AVISO_PERIODOS_SOBREPOSTOS,
  periodosStatusTemSobreposicao,
  ymdReferenciaAgendaStatusLocal,
} from '../lib/statusProgramadoUtils';
import { getEstabelecimentos } from '../lib/estabelecimentosStorage';
import { getCidadeById } from '../lib/experienciasStorage';

const MAX_IMAGE_SIZE_MB = 5;
const STEPS = ['Informações Básicas', 'Local e Data', 'Ingressos', 'Publicação', 'Status Programado'];

type ViewMode = 'list' | 'form';
type FormStep = 1 | 2 | 3 | 4 | 5;
type EventStatusComboPanel = 'header';

type ModoEventoOpcao = 'ativado' | 'naviex' | 'principal' | 'categoria' | 'desativar';

const EVENT_MODO_OPTIONS: { id: ModoEventoOpcao; label: string }[] = [
  { id: 'ativado', label: 'Ativado' },
  { id: 'naviex', label: 'Destaque Naviex' },
  { id: 'principal', label: 'Destaque Principal' },
  { id: 'categoria', label: 'Destaque Categoria' },
  { id: 'desativar', label: 'Desativado' },
];

function getModoEventoFromForm(f: Partial<Event>): ModoEventoOpcao {
  if (f.calendarioStatusPadrao === 'desativado') return 'desativar';
  if (f.destaqueNaviex) return 'naviex';
  if (f.layoutType === 'matéria_principal' || f.layoutType === 'slide_principal') return 'principal';
  if (f.destaqueSubcategoria) return 'categoria';
  return 'ativado';
}

function buildUpdateModoEvento(m: ModoEventoOpcao): Partial<Event> {
  switch (m) {
    case 'ativado':
      return {
        calendarioStatusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'desativar':
      return {
        calendarioStatusPadrao: 'desativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'principal':
      return {
        calendarioStatusPadrao: 'ativado',
        layoutType: 'matéria_principal',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'categoria':
      return {
        calendarioStatusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: true,
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'naviex':
      return {
        calendarioStatusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: true,
      };
    default:
      return {};
  }
}

function formatDateBR(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR');
}

const defaultForm: Partial<Event> = {
  title: '',
  shortDescription: '',
  description: '',
  coverImage: '',
  categoryId: '',
  tags: [],
  startDate: '',
  endDate: '',
  startTime: '09:00',
  endTime: '18:00',
  timezone: 'America/Sao_Paulo',
  format: 'presencial',
  isFree: true,
  maxParticipants: undefined,
  ticketTypes: [],
  visibility: 'publico',
  status: 'rascunho',
  estabelecimentoId: '',
  calendarioStatusPadrao: 'ativado',
  periodoAgendaDe: '',
  periodoAgendaAte: '',
  periodosStatus: [] as { de: string; ate: string; tipo: EventPeriodoStatusTipo }[],
  layoutType: 'normal',
  layoutExpiresAt: '',
  destaqueNaviex: false,
  destaqueNaviexExpiresAt: '',
  destaqueSubcategoria: false,
  destaqueSubcategoriaExpiresAt: '',
};

export default function EventosPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [categories, setCategories] = useState<ReturnType<typeof getEventCategories>>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [estabelecimentos, setEstabelecimentos] = useState<ReturnType<typeof getEstabelecimentos>>([]);
  const [form, setForm] = useState<Partial<Event>>({ ...defaultForm });
  const [step, setStep] = useState<FormStep>(1);
  const [query, setQuery] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [estabelecimentoQuery, setEstabelecimentoQuery] = useState('');
  const [estabelecimentoOpen, setEstabelecimentoOpen] = useState(false);
  const [statusComboContext, setStatusComboContext] = useState<EventStatusComboPanel | null>(null);
  const hojePeriodo = hojeYmdLocal();
  const isFirstFormRenderRef = useRef(true);
  const persistEventDraftRef = useRef<(opts?: { keepOnForm?: boolean }) => boolean>(() => false);
  const savePublishedEventRef = useRef<() => Promise<boolean>>(async () => false);
  const autoDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isDirty,
    setDirty,
    setIsPublishedLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
  } = useAdminUnsaved();
  const { showAlert, showConfirm } = useAdminDialog();

  const modoEventoAtual = useMemo(() => getModoEventoFromForm(form), [form]);

  useEffect(() => {
    setStatusComboContext(null);
  }, [step]);

  useEffect(() => {
    if (!statusComboContext) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-evento-status-combo-root]')) return;
      setStatusComboContext(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [statusComboContext]);

  const statusModoEventButtonClass = (m: ModoEventoOpcao) => {
    const base =
      'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all min-w-[11rem] max-w-[16rem] justify-between';
    if (m === 'desativar') return `${base} bg-red-500/15 border-red-500 text-red-800`;
    if (m === 'ativado') return `${base} bg-emerald-500/15 border-emerald-500 text-emerald-800`;
    return `${base} bg-amber-500/12 border-amber-500 text-amber-900`;
  };

  const statusModoEventDotClass = (m: ModoEventoOpcao) => {
    if (m === 'desativar') return 'bg-red-500';
    if (m === 'ativado') return 'bg-emerald-500';
    return 'bg-amber-500';
  };

  const renderEventoStatusCombo = () => (
      <div className="relative shrink-0" data-evento-status-combo-root>
        <button
          type="button"
          onClick={() => setStatusComboContext((c) => (c === 'header' ? null : 'header'))}
          className={statusModoEventButtonClass(modoEventoAtual)}
        >
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoEventDotClass(modoEventoAtual)}`} />
            <span className="truncate text-left">
              {EVENT_MODO_OPTIONS.find((o) => o.id === modoEventoAtual)?.label ?? 'Status'}
            </span>
          </span>
          <svg
            className={`w-4 h-4 shrink-0 ${statusComboContext === 'header' ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {statusComboContext === 'header' && (
          <div
            className="absolute top-full left-0 mt-1 py-1 rounded-lg border-2 border-gray-200 bg-white shadow-lg z-20 min-w-[15rem]"
          >
            {EVENT_MODO_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  updateForm(buildUpdateModoEvento(opt.id));
                  setStatusComboContext(null);
                  setDirty(true);
                }}
                className={`w-full inline-flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium ${
                  opt.id === modoEventoAtual ? 'bg-orange-50/80' : 'hover:bg-gray-50'
                }`}
              >
                <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoEventDotClass(opt.id)}`} />
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
  );

  const adicionarPeriodoEvento = async (tipo: EventPeriodoStatusTipo) => {
    let de = (form.periodoAgendaDe ?? '').trim();
    let ate = (form.periodoAgendaAte ?? '').trim();
    if (!de) return;
    if (!ate) ate = de;
    if (de > ate) [de, ate] = [ate, de];
    const umSoDia = de === ate;
    const periodos = form.periodosStatus ?? [];
    if (umSoDia) {
      if (tipo === 'ativado' || tipo === 'desativado') {
        updateForm({
          calendarioStatusPadrao: tipo === 'ativado' ? 'ativado' : 'desativado',
          periodoAgendaDe: '',
          periodoAgendaAte: '',
        });
      } else {
        const modo = tipo === 'naviex' ? 'naviex' : tipo === 'principal' ? 'principal' : 'categoria';
        updateForm({ ...buildUpdateModoEvento(modo), periodoAgendaDe: '', periodoAgendaAte: '' });
      }
      setDirty(true);
      return;
    }
    updateForm({
      periodosStatus: [...periodos, { de, ate, tipo }].sort((a, b) => a.de.localeCompare(b.de)),
      periodoAgendaDe: '',
      periodoAgendaAte: '',
    });
    setDirty(true);
  };

  const removerPeriodoEvento = (de: string, ate: string, tipo: EventPeriodoStatusTipo) => {
    const list = form.periodosStatus ?? [];
    const idx = list.findIndex((p) => p.de === de && p.ate === ate && p.tipo === tipo);
    if (idx < 0) return;
    updateForm({ periodosStatus: list.filter((_, i) => i !== idx) });
    setDirty(true);
  };

  const refresh = () => {
    setEvents(getEvents());
    setCategories(getEventCategories());
    setTags(getEventTags());
    setEstabelecimentos(getEstabelecimentos());
  };

  useEffect(() => {
    refresh();
  }, []);

  useRefetchWhenCatalogChanges(() => refresh());

  const hasProcessedNavState = useRef(false);
  useEffect(() => {
    const state = location.state as { startDate?: string; endDate?: string; estabelecimentoId?: string } | null;
    if (state?.startDate && !hasProcessedNavState.current) {
      hasProcessedNavState.current = true;
      openNew({ startDate: state.startDate, endDate: state.endDate ?? state.startDate, estabelecimentoId: state.estabelecimentoId });
      navigate('/eventos', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (viewMode !== 'form') return;
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, step, viewMode, setDirty]);

  const updateForm = (partial: Partial<Event>) => {
    setForm((prev) => ({ ...prev, ...partial }));
  };

  const openNew = (preset?: { startDate?: string; endDate?: string; estabelecimentoId?: string }) => {
    setEditingId(null);
    setForm({
      ...defaultForm,
      ...preset,
    } as Partial<Event>);
    setStep(1);
    setDirty(false);
    setEstabelecimentoQuery('');
    setEstabelecimentoOpen(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const openEdit = (id: string) => {
    const evt = getEventById(id);
    if (!evt) return;
    const evtWithCurrency = {
      ...evt,
      ticketTypes: (evt.ticketTypes ?? []).map((t) => ({ ...t, currency: t.currency ?? 'BRL' })),
    };
    setEditingId(id);
    setForm({ ...evtWithCurrency });
    setStep(1);
    setDirty(false);
    setEstabelecimentoQuery('');
    setEstabelecimentoOpen(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const handleCancel = async () => {
    if (isDirty) {
      const ok = await showConfirm('Ao sair, você perderá as informações não salvas. Deseja realmente sair?', {
        title: 'Alterações não salvas',
        confirmText: 'Sair',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }
    setDirty(false);
    setViewMode('list');
    setEditingId(null);
  };

  const requestStepChange = (nextStep: FormStep) => {
    setStep(nextStep);
  };

  const confirmEstabelecimentoAssociacao = async (): Promise<boolean> => {
    if (!form.estabelecimentoId || editingId) return true;
    const nome = estabelecimentoName(form.estabelecimentoId);
    return showConfirm(
      `Este evento será associado ao estabelecimento "${nome}". Confirma a criação?`,
      { title: 'Associar ao estabelecimento', confirmText: 'Confirmar', cancelText: 'Cancelar' }
    );
  };

  const handleSaveDraft = async () => {
    const result = validateEvent({ ...form, status: 'rascunho' });
    if (!result.ok) {
      await showAlert(result.errors.join('\n'), 'Erro de validação');
      return;
    }
    const ok = await confirmEstabelecimentoAssociacao();
    if (!ok) return;
    const payload: Omit<Event, 'id' | 'createdAt' | 'updatedAt'> = {
      ...form,
      status: 'rascunho',
    } as Omit<Event, 'id' | 'createdAt' | 'updatedAt'>;
    if (editingId) {
      const curr = getEventById(editingId);
      if (!curr) return;
      saveEvent({ ...curr, ...payload });
    } else {
      addEvent(payload);
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
  };

  const requestPublish = async () => {
    const result = validateEvent({ ...form, status: 'publicado' });
    if (!result.ok) {
      await showAlert(result.errors.join('\n'), 'Erro de validação');
      return;
    }
    const okAssoc = await confirmEstabelecimentoAssociacao();
    if (!okAssoc) return;
    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const ok = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }
    const payload: Omit<Event, 'id' | 'createdAt' | 'updatedAt'> = {
      ...form,
      status: 'publicado',
    } as Omit<Event, 'id' | 'createdAt' | 'updatedAt'>;
    if (editingId) {
      const curr = getEventById(editingId);
      if (!curr) return;
      saveEvent({ ...curr, ...payload });
    } else {
      addEvent(payload);
    }
    setShowPublishModal(false);
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
  };

  const persistEventDraftSilent = (opts?: { keepOnForm?: boolean }): boolean => {
    const payload = mergeEventDraftDefaults(form);
    try {
      if (editingId) {
        const curr = getEventById(editingId);
        if (!curr) return false;
        saveEvent({ ...curr, ...payload });
      } else {
        const added = addEvent(payload);
        if (opts?.keepOnForm) setEditingId(added.id);
      }
    } catch {
      return false;
    }
    setDirty(false);
    refresh();
    if (!opts?.keepOnForm) {
      setViewMode('list');
      setEditingId(null);
    }
    return true;
  };

  const savePublishedForLeave = async (): Promise<boolean> => {
    const result = validateEvent({ ...form, status: 'publicado' });
    if (!result.ok) {
      await showAlert(result.errors.join('\n'), 'Erro de validação');
      return false;
    }
    const okAssoc = await confirmEstabelecimentoAssociacao();
    if (!okAssoc) return false;
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const okOverlap = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!okOverlap) return false;
    }
    const payload: Omit<Event, 'id' | 'createdAt' | 'updatedAt'> = {
      ...form,
      status: 'publicado',
    } as Omit<Event, 'id' | 'createdAt' | 'updatedAt'>;
    if (editingId) {
      const curr = getEventById(editingId);
      if (!curr) return false;
      saveEvent({ ...curr, ...payload });
    } else {
      addEvent(payload);
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
    return true;
  };

  persistEventDraftRef.current = persistEventDraftSilent;
  savePublishedEventRef.current = savePublishedForLeave;

  useEffect(() => {
    if (viewMode !== 'form') {
      setIsPublishedLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      return;
    }
    const rascunho = form.status === 'rascunho';
    setIsPublishedLeave(Boolean(isDirty && form.status === 'publicado'));
    setFlushDraftHandler(
      rascunho && isDirty
        ? async () => persistEventDraftRef.current({ keepOnForm: true })
        : null
    );
    setSavePublishedHandler(
      form.status === 'publicado' && isDirty
        ? async () => savePublishedEventRef.current()
        : null
    );
    return () => {
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
    };
  }, [viewMode, form.status, isDirty, setIsPublishedLeave, setFlushDraftHandler, setSavePublishedHandler]);

  useEffect(() => {
    if (viewMode !== 'form' || form.status !== 'rascunho') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    if (isFirstFormRenderRef.current) return;
    if (!isDirty) return;
    if (autoDraftTimerRef.current) clearTimeout(autoDraftTimerRef.current);
    autoDraftTimerRef.current = setTimeout(() => {
      autoDraftTimerRef.current = null;
      void persistEventDraftRef.current({ keepOnForm: true });
    }, 1200);
    return () => {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
    };
  }, [form, viewMode, form.status, isDirty]);

  const handleDelete = async (evt: Event) => {
    const ok = await showConfirm(`Excluir o evento "${evt.title}"?`, {
      title: 'Excluir evento',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!ok) return;
    removeEvent(evt.id);
    refresh();
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      await showAlert(`A imagem excede ${MAX_IMAGE_SIZE_MB}MB.`);
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateForm({ coverImage: reader.result as string });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const addTag = (tag: string) => {
    const t = tag.trim();
    if (!t || form.tags?.includes(t)) return;
    updateForm({ tags: [...(form.tags ?? []), t] });
  };

  const removeTag = (tag: string) => {
    updateForm({ tags: form.tags?.filter((t) => t !== tag) ?? [] });
  };

  const addTicketType = () => {
    const newTicket: TicketType = {
      id: `tkt-${Date.now()}`,
      name: '',
      price: 0,
      quantity: 0,
      currency: 'BRL',
    };
    updateForm({ ticketTypes: [...(form.ticketTypes ?? []), newTicket] });
  };

  const updateTicketType = (id: string, partial: Partial<TicketType>) => {
    updateForm({
      ticketTypes: form.ticketTypes?.map((t) => (t.id === id ? { ...t, ...partial } : t)) ?? [],
    });
  };

  const removeTicketType = (id: string) => {
    const current = form.ticketTypes ?? [];
    if (current.length <= 1) return;
    updateForm({ ticketTypes: current.filter((t) => t.id !== id) });
  };

  const IconTrash = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
  );

  const filteredEvents = events.filter((e) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      e.title.toLowerCase().includes(q) ||
      e.shortDescription.toLowerCase().includes(q) ||
      e.tags.some((t) => t.toLowerCase().includes(q))
    );
  });

  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? '—';
  const estabelecimentoName = (id: string) => estabelecimentos.find((e) => e.id === id)?.nome ?? '—';
  const estabelecimentoDisplay = (id: string) => {
    const e = estabelecimentos.find((x) => x.id === id);
    if (!e) return '—';
    const cidade = e.cityId ? getCidadeById(e.cityId)?.cidade : null;
    return cidade ? `${e.nome} — ${cidade}` : e.nome;
  };
  const filteredEstabelecimentos = estabelecimentos.filter((e) => {
    const q = estabelecimentoQuery.trim().toLowerCase();
    if (!q) return true;
    const nome = e.nome.toLowerCase();
    const cidade = (e.cityId ? getCidadeById(e.cityId)?.cidade : '')?.toLowerCase() ?? '';
    return nome.includes(q) || cidade.includes(q);
  }).slice(0, 10);

  if (viewMode === 'list') {
    return (
      <AdminLayout>
        <div className="w-full min-w-0">
          <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
            <nav className="text-sm text-gray-500 mb-1">
              <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
              {' > '}
              <Link to="/eventos" className="hover:text-gray-700">Eventos</Link>
            </nav>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Eventos</h1>
              <button
                type="button"
                onClick={() => openNew()}
                className="px-4 py-2 min-h-[44px] bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
              >
                Novo Evento
              </button>
            </div>
          </div>
          <div className="px-4 sm:px-6 py-6">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar eventos..."
              className="w-full max-w-md py-2 px-3 rounded-lg border border-gray-300 mb-4"
            />
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="bg-orange-500 text-white">
                      <th className="text-left py-3 px-4 font-semibold">Título</th>
                      <th className="text-left py-3 px-4 font-semibold">Categoria</th>
                      <th className="text-left py-3 px-4 font-semibold">Data</th>
                      <th className="text-left py-3 px-4 font-semibold">Local</th>
                      <th className="text-left py-3 px-4 font-semibold">Status</th>
                      <th className="text-right py-3 px-4 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((evt, idx) => (
                      <tr key={evt.id} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">{evt.title}</div>
                          <div className="text-xs text-gray-500 line-clamp-1">{evt.shortDescription}</div>
                        </td>
                        <td className="py-3 px-4">{categoryName(evt.categoryId)}</td>
                        <td className="py-3 px-4">{formatDateBR(evt.startDate)}</td>
                        <td className="py-3 px-4">{evt.estabelecimentoId ? estabelecimentoName(evt.estabelecimentoId) : '—'}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            evt.status === 'publicado' ? 'bg-green-100 text-green-800' :
                            evt.status === 'rascunho' ? 'bg-gray-100 text-gray-600' :
                            evt.status === 'cancelado' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {evt.status === 'rascunho' ? 'Rascunho' : evt.status === 'publicado' ? 'Publicado' : evt.status === 'cancelado' ? 'Cancelado' : 'Finalizado'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(evt.id)}
                              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDelete(evt)}
                              className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredEvents.length === 0 && (
                <div className="p-8 text-center text-gray-500">Nenhum evento cadastrado.</div>
              )}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="w-full min-w-0">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
            {' > '}
            <Link to="/eventos" className="hover:text-gray-700">Eventos</Link>
            {' > '}
            <span className="text-gray-800">{editingId ? 'Editar evento' : 'Novo evento'}</span>
          </nav>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {editingId ? 'Editar evento' : 'Novo evento'}
              </h1>
              {renderEventoStatusCombo()}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handleCancel()}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleSaveDraft()}
                className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg font-medium hover:bg-orange-50"
              >
                Salvar como rascunho
              </button>
              <button
                type="button"
                onClick={() => void requestPublish()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
              >
                Publicar evento
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-6 max-w-4xl">
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {STEPS.map((s, i) => {
              const n = (i + 1) as FormStep;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => requestStepChange(n)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${step === n ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {s}
                </button>
              );
            })}
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-200 p-4 sm:p-6 space-y-6">
            {step === 1 && (
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Informações Básicas</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input
                    type="text"
                    value={form.title ?? ''}
                    onChange={(e) => updateForm({ title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Mínimo 5 caracteres"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição curta *</label>
                  <input
                    type="text"
                    value={form.shortDescription ?? ''}
                    onChange={(e) => updateForm({ shortDescription: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Máximo 160 caracteres"
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-500 mt-1">{(form.shortDescription ?? '').length}/160</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição completa *</label>
                  <textarea
                    rows={6}
                    value={form.description ?? ''}
                    onChange={(e) => updateForm({ description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Descrição detalhada do evento"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Imagem de capa *</label>
                  <input type="file" accept="image/*" onChange={handleCoverUpload} className="text-sm" />
                  {form.coverImage && (
                    <div className="mt-2">
                      <img src={form.coverImage} alt="Capa" className="max-h-40 rounded-lg border" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                  <select
                    value={form.categoryId ?? ''}
                    onChange={(e) => updateForm({ categoryId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Selecione</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <div className="flex gap-2 flex-wrap mb-2">
                    {(form.tags ?? []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-800 rounded text-sm">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="text-orange-600 hover:text-orange-800">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(tagInput), setTagInput(''))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Adicionar tag"
                    />
                    <button type="button" onClick={() => (addTag(tagInput), setTagInput(''))} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Tags disponíveis: {tags.slice(0, 8).join(', ')}...</p>
                </div>
              </section>
            )}

            {step === 2 && (
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Local e Data</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data início *</label>
                    <input
                      type="date"
                      value={form.startDate ?? ''}
                      onChange={(e) => updateForm({ startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data fim *</label>
                    <input
                      type="date"
                      value={form.endDate ?? ''}
                      onChange={(e) => updateForm({ endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horário início *</label>
                    <input
                      type="time"
                      value={form.startTime ?? '09:00'}
                      onChange={(e) => updateForm({ startTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horário fim *</label>
                    <input
                      type="time"
                      value={form.endTime ?? '18:00'}
                      onChange={(e) => updateForm({ endTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fuso horário *</label>
                  <select
                    value={form.timezone ?? 'America/Sao_Paulo'}
                    onChange={(e) => updateForm({ timezone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Formato do evento</label>
                  <select
                    value={form.format ?? 'presencial'}
                    onChange={(e) => updateForm({ format: e.target.value as EventFormat })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="presencial">Presencial</option>
                    <option value="online">Online</option>
                    <option value="hibrido">Híbrido</option>
                  </select>
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
                  <input
                    type="text"
                    value={form.estabelecimentoId
                      ? estabelecimentoDisplay(form.estabelecimentoId)
                      : estabelecimentoQuery}
                    onChange={(e) => {
                      setEstabelecimentoQuery(e.target.value);
                      updateForm({ estabelecimentoId: undefined });
                      setEstabelecimentoOpen(true);
                    }}
                    onFocus={() => setEstabelecimentoOpen(true)}
                    onBlur={() => setTimeout(() => setEstabelecimentoOpen(false), 150)}
                    placeholder="Digite para buscar (nome ou cidade)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {form.estabelecimentoId && (
                    <button
                      type="button"
                      onClick={() => { updateForm({ estabelecimentoId: undefined }); setEstabelecimentoQuery(''); }}
                      className="absolute right-2 top-8 text-gray-400 hover:text-gray-600"
                      aria-label="Limpar"
                    >
                      ×
                    </button>
                  )}
                  {estabelecimentoOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredEstabelecimentos.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum estabelecimento encontrado</div>
                      ) : (
                        filteredEstabelecimentos.map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 block"
                            onClick={() => {
                              updateForm({ estabelecimentoId: e.id });
                              setEstabelecimentoQuery('');
                              setEstabelecimentoOpen(false);
                            }}
                          >
                            {e.cityId ? `${e.nome} — ${getCidadeById(e.cityId)?.cidade ?? ''}` : e.nome}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}

            {step === 3 && (
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Ingressos</h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isFree ?? true}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const tickets = form.ticketTypes ?? [];
                      updateForm({
                        isFree: checked,
                        ticketTypes: checked ? [] : (tickets.length > 0 ? tickets : [{ id: `tkt-${Date.now()}`, name: '', price: 0, quantity: 0, currency: 'BRL' }]),
                      });
                    }}
                    className="rounded border-gray-300 text-orange-500"
                  />
                  <span className="font-medium">Entrada Gratuita</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máximo de participantes</label>
                  <input
                    type="number"
                    min={0}
                    value={form.maxParticipants ?? ''}
                    onChange={(e) => updateForm({ maxParticipants: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Opcional"
                  />
                </div>
                {!form.isFree && (
                  <div className="space-y-4">
                    {(form.ticketTypes ?? []).map((t) => (
                      <div key={t.id} className="flex gap-3 items-start">
                        <div className="flex-1 p-3 border border-gray-200 rounded-lg space-y-2 min-w-0">
                          <input
                            type="text"
                            placeholder="Nome (ex: Inteira)"
                            value={t.name}
                            onChange={(e) => updateTicketType(t.id, { name: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            <div>
                              <label className="text-xs">Preço</label>
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                value={t.price || ''}
                                onChange={(e) => updateTicketType(t.id, { price: Number(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs">Moeda</label>
                              <select
                                value={t.currency ?? 'BRL'}
                                onChange={(e) => updateTicketType(t.id, { currency: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                              >
                                <option value="BRL">BRL</option>
                                <option value="USD">USD</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-xs">Quantidade</label>
                              <input
                                type="number"
                                min={1}
                                value={t.quantity || ''}
                                onChange={(e) => updateTicketType(t.id, { quantity: Number(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                              />
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTicketType(t.id)}
                          disabled={(form.ticketTypes ?? []).length <= 1}
                          className={`p-2 rounded-lg shrink-0 mt-1 ${(form.ticketTypes ?? []).length <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                          title={(form.ticketTypes ?? []).length <= 1 ? 'Mínimo um tipo de ingresso' : 'Remover'}
                          aria-label="Remover"
                        >
                          <IconTrash />
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={addTicketType}
                        className="flex items-center justify-center w-10 h-10 rounded-lg border-2 border-dashed border-orange-400 text-orange-600 hover:bg-orange-50 shrink-0"
                        title="Adicionar ingresso"
                        aria-label="Adicionar ingresso"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {step === 4 && (
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Publicação</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visibilidade</label>
                  <select
                    value={form.visibility ?? 'publico'}
                    onChange={(e) => updateForm({ visibility: e.target.value as EventVisibility })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="publico">Público</option>
                    <option value="privado">Privado</option>
                  </select>
                  {form.visibility === 'privado' && (
                    <p className="mt-1.5 text-xs text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <strong>Publicação Privada — quem poderá ver?</strong><br />
                      Apenas você e outros administradores do painel. O evento não aparecerá na listagem pública nem para visitantes.
                    </p>
                  )}
                </div>
              </section>
            )}

            {step === 5 && (
              <section className="space-y-4 bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 tracking-wide mb-1">Status Programado</h2>
                <div className="text-xs font-semibold tracking-wide text-gray-600 mb-1">Status Programado</div>
                <p className="text-sm text-gray-600 mb-3">Agende visibilidade e destaques no calendário (mesma regra que notícias, hospedagem e estabelecimentos).</p>
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                  <div className="min-w-0 max-w-md">
                    <span className="block text-sm font-medium text-gray-700 mb-2">Período (Data)</span>
                    <PeriodoDataCalendar
                      minDate={hojePeriodo}
                      periodoDe={form.periodoAgendaDe ?? ''}
                      periodoAte={form.periodoAgendaAte ?? ''}
                      periodosAgendaVisual={form.periodosStatus ?? []}
                      diaReferenciaAgendaVisual={ymdReferenciaAgendaStatusLocal()}
                      onChange={(de, ate) => updateForm({ periodoAgendaDe: de, periodoAgendaAte: ate })}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 lg:pt-7">
                    <button type="button" onClick={() => void adicionarPeriodoEvento('ativado')} className="px-3 py-2 rounded-lg text-sm font-medium border border-emerald-300 bg-emerald-50 text-emerald-800">Ativar</button>
                    <button type="button" onClick={() => void adicionarPeriodoEvento('naviex')} className="px-3 py-2 rounded-lg text-sm font-medium border border-violet-300 bg-violet-50 text-violet-800">Destaque Naviex</button>
                    <button type="button" onClick={() => void adicionarPeriodoEvento('principal')} className="px-3 py-2 rounded-lg text-sm font-medium border border-amber-400 bg-amber-50 text-amber-900">Destaque Principal</button>
                    <button type="button" onClick={() => void adicionarPeriodoEvento('categoria')} className="px-3 py-2 rounded-lg text-sm font-medium border border-orange-300 bg-orange-50 text-orange-900">Destaque Categoria</button>
                    <button type="button" onClick={() => void adicionarPeriodoEvento('desativado')} className="px-3 py-2 rounded-lg text-sm font-medium border border-red-300 bg-red-50 text-red-800">Desativado</button>
                  </div>
                </div>
                {(form.periodosStatus ?? []).filter((p) => p.ate >= hojePeriodo).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {(form.periodosStatus ?? [])
                      .filter((p) => p.ate >= hojePeriodo)
                      .map((p) => (
                        <li key={`${p.de}-${p.ate}-${p.tipo}`} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white border text-sm">
                          <span>
                            {p.de === p.ate ? p.de : `${p.de} — ${p.ate}`}{' '}
                            <span className="text-xs font-medium text-gray-600">({p.tipo})</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removerPeriodoEvento(p.de, p.ate, p.tipo)}
                            className="text-red-600 hover:bg-red-50 rounded p-1"
                            aria-label="Remover"
                          >
                            ×
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
              </section>
            )}

            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => requestStepChange((step - 1) as FormStep)}
                  className="px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  ← Anterior
                </button>
              )}
              {step < 5 && (
                <button
                  type="button"
                  onClick={() => requestStepChange((step + 1) as FormStep)}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
                >
                  Próximo →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPublishModal && (
        <div className="fixed inset-0 bg-black/70 z-[300] flex items-center justify-center p-4" onClick={() => setShowPublishModal(false)}>
          <div className="bg-gray-900 text-gray-100 rounded-2xl shadow-xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 space-y-3 text-sm">
              <h3 className="text-lg font-semibold text-white mb-4">Resumo do evento</h3>
              <p><strong className="text-gray-300">Título:</strong> {form.title || '—'}</p>
              <p><strong className="text-gray-300">Descrição curta:</strong> {form.shortDescription || '—'}</p>
              <p><strong className="text-gray-300">Categoria:</strong> {categoryName(form.categoryId ?? '')}</p>
              <p><strong className="text-gray-300">Data:</strong> {form.startDate} a {form.endDate}</p>
              <p><strong className="text-gray-300">Horário:</strong> {form.startTime} - {form.endTime}</p>
              <p><strong className="text-gray-300">Formato:</strong> {form.format === 'presencial' ? 'Presencial' : form.format === 'online' ? 'Online' : 'Híbrido'}</p>
              <p><strong className="text-gray-300">Local:</strong> {form.estabelecimentoId ? estabelecimentoDisplay(form.estabelecimentoId) : '—'}</p>
              <p><strong className="text-gray-300">Gratuito:</strong> {form.isFree ? 'Sim' : 'Não'}</p>
              {!form.isFree && form.ticketTypes && form.ticketTypes.length > 0 && (
                <p><strong className="text-gray-300">Ingressos:</strong> {form.ticketTypes.map((t) => `${t.name} ${t.currency ?? 'BRL'} ${t.price}`).join(', ')}</p>
              )}
              <p><strong className="text-gray-300">Visibilidade:</strong> {form.visibility === 'publico' ? 'Público' : 'Privado'}</p>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setShowPublishModal(false)}
                className="px-4 py-2 border border-gray-600 rounded-lg font-medium text-gray-200 hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmPublish()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
              >
                Publicar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
